import { useCallback, useEffect, useRef, useState } from "react";
import { requestBlogCmsTicket } from "@/services/blog-cms";
import {
  BLOG_CMS_READY_TIMEOUT_MS,
  BLOG_CMS_REAUTH_READY_TIMEOUT_MS,
  BLOG_CMS_RENEW_INTERVAL_MS,
  BLOG_CMS_RENEW_RETRY_MS,
  BLOG_CMS_RENEW_TIMEOUT_MS,
  classifyTicketFailure,
  frameOriginOf,
  readBlogCmsFrameMessage,
  reauthTicketRequest,
  renewTicketRequest,
  type BlogCmsFrameMessage,
  type TicketFailure,
} from "@/lib/blog-cms/protocol";
import { takeReauthSlot } from "@/lib/blog-cms/reauth-budget";
import { rememberBlogCmsOrigin } from "@/lib/blog-cms/session-logout";

/** `src` is null while the renewal's ticket is pending. */
export type BlogCmsRenewal = { trigger: "timer" | "reauth"; src: string | null };

export type BlogCmsFrameState =
  /** B1: the first ticket is pending. */
  | { kind: "ticket" }
  /**
   * The editor frame. B1 overlay while `!ready && !silent`; an editor re-ticket while
   * `!ready && silent`; B2 once ready. `renewal` is the hidden-frame C5 renewal (U1), and
   * `renewFailure` the V11 inline alert over the still-mounted editor.
   */
  | {
      kind: "frame";
      src: string;
      origin: string;
      ready: boolean;
      silent: boolean;
      renewal: BlogCmsRenewal | null;
      renewDueAt: number | null;
      renewRetried: boolean;
      notice: "reconnected" | null;
      renewFailure: { failure: TicketFailure; seq: number } | null;
    }
  /** B4. `seq` keys the panel so a new refusal restarts its countdown. */
  | { kind: "refused"; failure: TicketFailure | { reason: "reauth-exhausted" }; seq: number }
  /** B5. */
  | { kind: "unreachable" };

type FrameState = Extract<BlogCmsFrameState, { kind: "frame" }>;
type RenewingState = FrameState & { renewal: BlogCmsRenewal };

const INITIAL_STATE: BlogCmsFrameState = { kind: "ticket" };

function freshFrame(src: string, origin: string): FrameState {
  return {
    kind: "frame",
    src,
    origin,
    ready: false,
    silent: false,
    renewal: null,
    renewDueAt: null,
    renewRetried: false,
    notice: null,
    renewFailure: null,
  };
}

/**
 * The `/blog-cms` state machine (admin plan §2.4). Every transition runs from an event, a timer or
 * a promise callback, never synchronously in an effect body. `stateRef` mirrors the state so those
 * callbacks read the latest value instead of a stale closure.
 */
export function useBlogCmsFrame(options: { enabled: boolean }): {
  state: BlogCmsFrameState;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  renewFrameRef: React.RefObject<HTMLIFrameElement | null>;
  retry: () => void;
  retryRenewal: () => void;
} {
  const { enabled } = options;
  const [state, setState] = useState<BlogCmsFrameState>(INITIAL_STATE);
  const [attempt, setAttempt] = useState(0);
  const stateRef = useRef<BlogCmsFrameState>(INITIAL_STATE);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const renewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const seqRef = useRef(0);
  const reauthHistoryRef = useRef<number[]>([]);
  const renewGenRef = useRef(0);
  const renewControllerRef = useRef<AbortController | null>(null);
  const reticketGenRef = useRef(0);
  const reticketControllerRef = useRef<AbortController | null>(null);

  /**
   * The state while a frame-only callback runs. Every such callback is reachable only in `frame`:
   * its effect is torn down, or its generation made stale, by every transition out of it.
   */
  const frameState = useCallback(() => stateRef.current as FrameState, []);
  /** As `frameState`, for callbacks that run only while a renewal is in flight. */
  const renewingState = useCallback(() => stateRef.current as RenewingState, []);

  const commit = useCallback((next: BlogCmsFrameState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const nextSeq = useCallback(() => {
    seqRef.current += 1;
    return seqRef.current;
  }, []);

  /** Makes every in-flight renewal and re-ticket stale (RV3.9). */
  const abortInFlight = useCallback(() => {
    renewGenRef.current += 1;
    renewControllerRef.current?.abort();
    renewControllerRef.current = null;
    reticketGenRef.current += 1;
    reticketControllerRef.current?.abort();
    reticketControllerRef.current = null;
  }, []);

  const refuse = useCallback(
    (failure: TicketFailure | { reason: "reauth-exhausted" }) => {
      abortInFlight();
      commit({ kind: "refused", failure, seq: nextSeq() });
    },
    [abortInFlight, commit, nextSeq],
  );

  /** Runs only while a renewal is in flight: from its own generation-checked ticket or watchdog. */
  const failRenewal = useCallback(
    (failure: TicketFailure) => {
      const current = renewingState();
      renewGenRef.current += 1;
      renewControllerRef.current = null;
      if (current.renewal.trigger === "reauth") {
        // V11: the editor stays mounted, so its unsaved text survives.
        commit({ ...current, renewal: null, renewFailure: { failure, seq: nextSeq() } });
      } else if (!current.renewRetried) {
        // A8: one retry inside C5's remaining life before B4 throws the editor away.
        commit({
          ...current,
          renewal: null,
          renewRetried: true,
          renewDueAt: Date.now() + BLOG_CMS_RENEW_RETRY_MS,
        });
      } else {
        refuse(failure);
      }
    },
    [commit, nextSeq, refuse, renewingState],
  );

  const startRenewal = useCallback(
    (trigger: BlogCmsRenewal["trigger"]) => {
      const current = frameState();
      renewGenRef.current += 1;
      const generation = renewGenRef.current;
      renewControllerRef.current?.abort();
      const controller = new AbortController();
      renewControllerRef.current = controller;
      commit({ ...current, renewal: { trigger, src: null }, notice: null, renewFailure: null });

      requestBlogCmsTicket(renewTicketRequest(), controller.signal).then(
        ({ url }) => {
          if (generation !== renewGenRef.current) return;
          // A current generation means this renewal is still the one in flight.
          const latest = renewingState();
          rememberBlogCmsOrigin(frameOriginOf(url));
          commit({ ...latest, renewal: { ...latest.renewal, src: url } });
        },
        (error: unknown) => {
          if (generation !== renewGenRef.current) return;
          failRenewal(classifyTicketFailure(error));
        },
      );
    },
    [commit, failRenewal, frameState, renewingState],
  );

  const reticketEditor = useCallback(
    (next: string) => {
      abortInFlight();
      const generation = reticketGenRef.current;
      const controller = new AbortController();
      reticketControllerRef.current = controller;
      commit({ ...frameState(), renewal: null });

      requestBlogCmsTicket(reauthTicketRequest(next), controller.signal).then(
        ({ url }) => {
          if (generation !== reticketGenRef.current) return;
          const latest = frameState();
          const origin = frameOriginOf(url);
          rememberBlogCmsOrigin(origin);
          commit({
            ...latest,
            src: url,
            origin,
            ready: false,
            silent: true,
            renewal: null,
            renewDueAt: null,
            renewFailure: null,
            notice: null,
          });
        },
        (error: unknown) => {
          if (generation !== reticketGenRef.current) return;
          refuse(classifyTicketFailure(error));
        },
      );
    },
    [abortInFlight, commit, frameState, refuse],
  );

  const handleEditorMessage = useCallback(
    (message: BlogCmsFrameMessage, current: FrameState) => {
      if (message.type === "blog-cms:ready") {
        // A ready after a ticket redemption means C5 was just set; a repeat ready does not.
        commit({
          ...current,
          ready: true,
          silent: false,
          renewDueAt: current.ready ? current.renewDueAt : Date.now() + BLOG_CMS_RENEW_INTERVAL_MS,
        });
        return;
      }

      if (message.type !== "blog-cms:reauth") return;

      if (message.inPlace && current.renewal !== null) {
        // Joins the renewal in flight: no slot, and a failure now keeps the editor (V11).
        commit({ ...current, renewal: { ...current.renewal, trigger: "reauth" } });
        return;
      }

      const slot = takeReauthSlot(reauthHistoryRef.current, Date.now());
      reauthHistoryRef.current = slot.history;
      if (!slot.allowed) {
        refuse({ reason: "reauth-exhausted" });
        return;
      }

      if (message.inPlace) {
        startRenewal("reauth");
      } else {
        // A10: S3 is rendered in the frame, so there is no editor state to keep.
        reticketEditor(message.next);
      }
    },
    [commit, refuse, reticketEditor, startRenewal],
  );

  const handleRenewed = useCallback(
    (current: FrameState) => {
      // Null when a second `renewed` lands before React has unmounted the hidden frame.
      if (current.renewal === null) return;
      renewGenRef.current += 1;
      renewControllerRef.current = null;
      commit({
        ...current,
        renewal: null,
        renewDueAt: Date.now() + BLOG_CMS_RENEW_INTERVAL_MS,
        renewRetried: false,
        renewFailure: null,
        notice: current.renewal.trigger === "reauth" ? "reconnected" : null,
      });
    },
    [commit],
  );

  // Ticket effect. The abort matters: StrictMode double-mounts would otherwise mint two tickets.
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let active = true;
    requestBlogCmsTicket({}, controller.signal).then(
      ({ url }) => {
        if (!active) return;
        const origin = frameOriginOf(url);
        rememberBlogCmsOrigin(origin);
        commit(freshFrame(url, origin));
      },
      (error: unknown) => {
        if (!active) return;
        commit({ kind: "refused", failure: classifyTicketFailure(error), seq: nextSeq() });
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [enabled, attempt, commit, nextSeq]);

  const inFrame = state.kind === "frame";
  const waitingForReady = state.kind === "frame" && !state.ready;
  const silent = state.kind === "frame" && state.silent;
  const frameSrc = state.kind === "frame" ? state.src : null;

  // Ready watchdog: B5 after 30 s on a cold load, 15 s after an editor re-ticket.
  useEffect(() => {
    if (!waitingForReady) return;
    const timer = setTimeout(
      () => {
        abortInFlight();
        commit({ kind: "unreachable" });
      },
      silent ? BLOG_CMS_REAUTH_READY_TIMEOUT_MS : BLOG_CMS_READY_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [waitingForReady, silent, frameSrc, abortInFlight, commit]);

  // C3 listener. `ready`/`reauth` count only from the editor, `renewed` only from the hidden frame.
  useEffect(() => {
    if (!inFrame) return;
    function onMessage(event: MessageEvent) {
      const current = stateRef.current;
      if (current.kind !== "frame") return;
      const fromEditor = readBlogCmsFrameMessage(event, {
        origin: current.origin,
        source: iframeRef.current?.contentWindow ?? null,
      });
      if (fromEditor !== null) {
        handleEditorMessage(fromEditor, current);
        return;
      }
      const fromRenewal = readBlogCmsFrameMessage(event, {
        origin: current.origin,
        source: renewFrameRef.current?.contentWindow ?? null,
      });
      if (fromRenewal?.type === "blog-cms:renewed") {
        handleRenewed(current);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [inFrame, handleEditorMessage, handleRenewed]);

  const renewDueAt =
    state.kind === "frame" && state.ready && state.renewal === null ? state.renewDueAt : null;

  // Renewal timer (U1).
  useEffect(() => {
    if (renewDueAt === null) return;
    const timer = setTimeout(() => startRenewal("timer"), Math.max(0, renewDueAt - Date.now()));
    return () => clearTimeout(timer);
  }, [renewDueAt, startRenewal]);

  const renewalSrc = state.kind === "frame" ? (state.renewal?.src ?? null) : null;

  // Renewal watchdog: no `renewed` within 15 s of the hidden frame mounting fails the renewal.
  useEffect(() => {
    if (renewalSrc === null) return;
    const timer = setTimeout(() => failRenewal({ reason: "failed" }), BLOG_CMS_RENEW_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [renewalSrc, failRenewal]);

  // Unmount: late results of anything in flight are ignored.
  useEffect(() => abortInFlight, [abortInFlight]);

  const retry = useCallback(() => {
    // The re-auth history is kept: the cap is per 5 minutes, not per attempt.
    abortInFlight();
    commit({ kind: "ticket" });
    setAttempt((value) => value + 1);
  }, [abortInFlight, commit]);

  const retryRenewal = useCallback(() => {
    const current = stateRef.current;
    if (current.kind !== "frame" || current.renewFailure === null || current.renewal !== null)
      return;
    // A user's click is not a loop, so it takes no cap slot (V11).
    startRenewal("reauth");
  }, [startRenewal]);

  return { state, iframeRef, renewFrameRef, retry, retryRenewal };
}
